const FALLBACK = '/dashboard/assistants'
const STORAGE_KEY = 'auth_redirect_path'
const RECONNECT_STORAGE_KEY = 'botnexus:reconnect:return-path'
const LOCAL_ORIGIN = 'https://portal.invalid'

/** Accept a local destination, rejecting browser URL normalization tricks. */
export function safeLocalPath(value: unknown, allowEncodedSeparators = false): string | null {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null
  if (/[\\\u0000-\u0020\u007f]/.test(value) || /%(?:0[0-9a-f]|1[0-9a-f]|7f)/i.test(value.split(/[?#]/)[0])) return null
  if (!allowEncodedSeparators && /%(?:2f|5c)/i.test(value.split(/[?#]/)[0])) return null
  try {
    const url = new URL(value, LOCAL_ORIGIN)
    if (url.origin !== LOCAL_ORIGIN) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

export function safePortalReturnPath(value: unknown): string | null {
  // Encoded slashes are valid assistant identifier data in this one route;
  // they cannot change the origin or the literal browser pathname structure.
  const reconnect = typeof value === 'string' && /^\/auth\/reconnect\/[^/?#]+\/?(?:[?#].*)?$/.test(value)
  const path = safeLocalPath(value, reconnect)
  if (!path) return null
  const pathname = new URL(path, LOCAL_ORIGIN).pathname
  // Only known authenticated destinations can follow login. In particular,
  // login, OAuth callbacks and the root redirect must never redirect to themselves.
  return /^\/dashboard(?:\/|$)/.test(pathname) ||
    /^\/auth\/reconnect\/[^/]+\/?$/.test(pathname) ||
    pathname === '/integrations/callback' ? path : null
}

export function scholarReturnPath(value: unknown): string {
  const path = safeLocalPath(value)
  if (!path) return '/chat'
  const pathname = new URL(path, LOCAL_ORIGIN).pathname
  return /^\/chat(?:\/|$)/.test(pathname) ? path : '/chat'
}

function readStoredPath(): string | null {
  try {
    const path = safePortalReturnPath(localStorage.getItem(STORAGE_KEY))
    // Never import another tab's reconnect intent from the legacy shared key.
    return isReconnect(path) ? null : path
  } catch { return null }
}

function isReconnect(path: string | null): boolean {
  return !!path?.startsWith('/auth/reconnect/')
}

function readReconnectPath(): string | null {
  try {
    const path = safePortalReturnPath(sessionStorage.getItem(RECONNECT_STORAGE_KEY))
    return isReconnect(path) ? path : null
  } catch { return null }
}

function captureCurrentReconnectPath(): string | null {
  if (typeof window === 'undefined') return null
  const { pathname, search, hash } = window.location
  const path = safePortalReturnPath(`${pathname}${search}${hash}`)
  if (!path || !isReconnect(path)) return null
  // AuthProvider can reject an old portal token before protected routes mount
  // and navigate to login without route state. Persist the original intent now.
  try { sessionStorage.setItem(RECONNECT_STORAGE_KEY, path) } catch { /* Keep the in-memory capture. */ }
  return path
}

// Capture before auth initialization can clear storage or replace the route.
// A fresh reconnect URL always supersedes an older pending intent in this tab.
let capturedPath = captureCurrentReconnectPath() || readReconnectPath() || readStoredPath()

export function getAuthReturnPath(state: unknown): string {
  const from = (state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from
  const statePath = from && typeof from.pathname === 'string'
    ? safePortalReturnPath(`${from.pathname}${from.search || ''}${from.hash || ''}`)
    : null
  return statePath || readReconnectPath() || capturedPath || readStoredPath() || FALLBACK
}

export function rememberAuthReturnPath(state: unknown): string {
  const path = getAuthReturnPath(state)
  capturedPath = path
  try {
    if (isReconnect(path)) {
      sessionStorage.setItem(RECONNECT_STORAGE_KEY, path)
    } else {
      sessionStorage.removeItem(RECONNECT_STORAGE_KEY)
      localStorage.setItem(STORAGE_KEY, path)
    }
  } catch { /* Navigation still works in this tab. */ }
  return path
}

export function clearAuthReturnPath(consumedPath = readReconnectPath() || capturedPath) {
  capturedPath = null
  try { sessionStorage.removeItem(RECONNECT_STORAGE_KEY) } catch { /* Storage may be unavailable. */ }
  if (consumedPath && !isReconnect(consumedPath)) {
    try {
      if (localStorage.getItem(STORAGE_KEY) === consumedPath) localStorage.removeItem(STORAGE_KEY)
    } catch { /* Storage may be unavailable. */ }
  }
}

export function clearReconnectReturnPath() {
  if (isReconnect(capturedPath)) capturedPath = null
  try { sessionStorage.removeItem(RECONNECT_STORAGE_KEY) } catch { /* Storage may be unavailable. */ }
}
